import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from './ui/button';
import { GripVertical, Trash2 } from 'lucide-react';
import RowStyleMenu from './RowStyleMenu';

const autoResize = (el) => {
  if (el) {
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }
};

function ScheduleRow({ row, columns, dayId, onUpdateRow, onRemoveRow, canRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: row.id, data: { type: 'row', dayId } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const cols = Array.isArray(columns) ? columns : [];
  const fmt = {
    fontWeight: row.bold ? 700 : undefined,
    fontStyle: row.italic ? 'italic' : undefined,
  };
  const markerStyle = row.color ? { borderLeft: `4px solid ${row.color}` } : undefined;

  const styleMenu = (
    <RowStyleMenu
      bold={!!row.bold}
      italic={!!row.italic}
      color={row.color || null}
      onChange={(s) => onUpdateRow({ ...row, bold: s.bold, italic: s.italic, color: s.color })}
    />
  );

  const actionsCell = (
    <td className="no-print">
      <div className="flex items-center gap-0.5 justify-end">
        {styleMenu}
        {canRemove && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemoveRow}
            className="text-slate-400 hover:text-red-600 px-1"
            data-testid="remove-row-button"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </td>
  );

  if (row.type === 'text') {
    const textVal = row.text != null ? row.text : (row.notes || '');
    return (
      <tr ref={setNodeRef} style={style} className="text-row" data-testid="text-row">
        <td className="no-print" style={markerStyle}>
          <div
            {...attributes}
            {...listeners}
            className="drag-handle cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600"
            data-testid="row-drag-handle"
          >
            <GripVertical className="h-4 w-4" />
          </div>
        </td>
        <td colSpan={Math.max(1, cols.length)}>
          <input
            type="text"
            value={textVal}
            onChange={(e) => onUpdateRow({ ...row, text: e.target.value })}
            placeholder="Abschnitts-Überschrift oder Hinweis..."
            className="font-semibold"
            style={fmt}
            data-testid="text-row-input"
          />
        </td>
        {actionsCell}
      </tr>
    );
  }

  return (
    <tr ref={setNodeRef} style={style} data-testid="schedule-row">
      <td className="no-print" style={markerStyle}>
        <div
          {...attributes}
          {...listeners}
          className="drag-handle cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600"
          data-testid="row-drag-handle"
        >
          <GripVertical className="h-4 w-4" />
        </div>
      </td>
      {cols.map((col) => (
        <td key={col.id}>
          <textarea
            ref={autoResize}
            value={row[col.id] || ''}
            onChange={(e) => onUpdateRow({ ...row, [col.id]: e.target.value })}
            onInput={(e) => autoResize(e.target)}
            placeholder={col.label}
            style={{ minHeight: '24px', height: 'auto', resize: 'none', overflow: 'hidden', ...fmt }}
            data-testid={`cell-${col.id}`}
          />
        </td>
      ))}
      {actionsCell}
    </tr>
  );
}

export default ScheduleRow;
